/* eslint-disable jsx-a11y/no-static-element-interactions */
import _ from 'underscore';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { Link } from '@bingads-webui-react/router';
import { submitEvent } from '@bingads-webui-universal/accessibility-helper';
import { DefaultButton } from '@bingads-webui-react/fabric-control';
import { Glyphicon, Tooltip, OverlayTrigger } from '@bingads-webui-react/primitive';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withOverflowProp } from '@bingads-webui-react/with-overflow-prop';
import { getBreadcrumbStyles } from './styles';

class Breadcrumb extends React.PureComponent {
  onClickHandler = (item, dismissMenu) => {
    if (item.onClick) {
      item.onClick(item);
    }

    dismissMenu(null, true);
  }

  renderBreadcrumbItem = (item, dismissMenu = _.noop) => {
    const clickable = _.isFunction(item.onClick);

    const className = classNames(
      'breadcrumb-item-text',
      item.isCurrentItem ? 'current-breadcrumb' : '',
      item.isLongText ? 'long-text' : '',
      clickable ? 'clickable-item' : ''
    );

    const { tooltipPlacement } = this.props;

    const BreadcrumbItem = ({ forwardedRef, isTextOverflow }) => (
      // TODO: currently tabIndex 0 and stopPropagation() is needed to make sure this breadcrumb
      // can trigger onClick with Icon-Button. Need to undersand the root cause and remove them.
      <OverlayTrigger
        placement={tooltipPlacement}
        overlay={
          <Tooltip id={`tooltip-${tooltipPlacement}`} className={isTextOverflow ? '' : 'hide'}>
            {item.text}
          </Tooltip>
        }
      >
        <div
          className="breadcrumb-text-container"
          key={item.key}
          onClick={() => this.onClickHandler(item, dismissMenu)}
          onKeyDown={submitEvent(() => this.onClickHandler(item, dismissMenu))}
          aria-label={item['aria-label'] || null}
          role={clickable ? 'button' : null}
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={clickable ? 0 : null}
        >
          <div className={className} ref={forwardedRef}>
            {item.text}
          </div>
        </div>
      </OverlayTrigger>
    );
    const BreadcrumbItemWithOverflow = withOverflowProp(BreadcrumbItem);

    if (item.url && !clickable) {
      return (
        <Link
          className={item.key}
          key={item.key}
          to={item.url}
          onClick={() => dismissMenu(null, true)}
        >
          <BreadcrumbItemWithOverflow />
        </Link>
      );
    }

    return (
      <BreadcrumbItemWithOverflow />
    );
  };

  renderViewMoreDropdown = (overflowItems) => {
    const {
      overflowAriaLabel,
      renderDivider,
    } = this.props;

    const menuProps = { doNotLayer: true };
    menuProps.items = [];
    _.each(overflowItems, (item, index) => {
      menuProps.items[index] = { ...item };

      menuProps.items[index].onRender
        = (menuItem, dismissMenu) => this.renderBreadcrumbItem(menuItem, dismissMenu);
    });

    return (
      <div className="breadcrumb-item">
        <DefaultButton
          menuProps={menuProps}
          id="breadcrumb-more"
          key="breadcrumb-more"
          persistMenu
          onRenderMenuIcon={_.noop}
          onRenderIcon={() => (<Glyphicon
            className="More"
            glyph="ba-More"
          />)}
          className="breadcrumb-more"
          ariaLabel={overflowAriaLabel}
          shouldFocusOnMount
        />
        {renderDivider()}
      </div>
    );
  }

  render() {
    const {
      classes,
      items,
      maxDisplayedItems,
      renderDivider,
    } = this.props;

    const renderItems = [...items];
    let overflowItems = [];
    if (maxDisplayedItems && maxDisplayedItems < items.length) {
      // TODO: Implement responsive overflowItems when resize the window
      overflowItems = renderItems.splice(0, renderItems.length - maxDisplayedItems);
    }

    const breadcrumbItems = renderItems.map((breadcrumb, index) => (
      <div key={_.uniqueId('breadcrumb-item-')} className="breadcrumb-item">
        {this.renderBreadcrumbItem(breadcrumb)}
        {index !== renderItems.length - 1 && renderDivider()}
      </div>
    ));

    return (
      <div className={classNames(classes.breadcrumbContainer, 'breadcrumb-container')}>
        <div className="breadcrumb">
          {overflowItems.length > 0 && this.renderViewMoreDropdown(overflowItems)}
          {breadcrumbItems}
        </div>
      </div>
    );
  }
}

Breadcrumb.propTypes = {
  /**
   * Define the classes for Breadcrumb container, Breadcrumb container name can be customized.
   * Default value is 'breadcrumbContainer'
   */
  classes: PropTypes.shape(_.mapObject(
    getBreadcrumbStyles({}, {}),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
  /**
   * Collection of breadcrumbs to render
   */
  items: PropTypes.arrayOf(PropTypes.shape({
    /**
     * Text to display to the user for the breadcrumb
     */
    text: PropTypes.string.isRequired,
    /**
     * Unique string associated with breadcrumb
     */
    key: PropTypes.string.isRequired,
    /**
     * Callback when the breadcrumb is selected.
     */
    onClick: PropTypes.func,
    /**
     * Url to navigate to when this breadcrumb is clicked.
     */
    url: PropTypes.string,
    /**
     * Indicate this breadcrumb item is the item the user is currently on
     */
    isCurrentItem: PropTypes.bool,
    /**
     * Indicate this breadcrumb item is long and should have a longer width before seeing ellipsis
     */
    isLongText: PropTypes.bool,
    /**
     * Aria label of breadcrumb.
     */
    'aria-label': PropTypes.string,
  })).isRequired,
  /**
   * Render a custom divider in place of the default chevron >
   */
  renderDivider: PropTypes.func,
  /**
   * The maximum number of breadcrumbs to display before coalescing.
   */
  maxDisplayedItems: PropTypes.number,
  /**
   * Aria label on overflow button.
   */
  overflowAriaLabel: PropTypes.string,
  /**
   * tooltip placement default value is top.
   */
  tooltipPlacement: PropTypes.string,
};

Breadcrumb.defaultProps = {
  renderDivider: () => (
    <div className="breadcrumb-divider">
      <Glyphicon
        className="chevron-right-icon"
        glyph="ba-ChevronRight"
      />
    </div>
  ),
  maxDisplayedItems: _.noop(),
  overflowAriaLabel: '',
  tooltipPlacement: 'top',
};

export const StyledBreadcrumb = (props) => {
  const classes = useDefaultStyles(getBreadcrumbStyles);
  return <Breadcrumb {...props} classes={classes} />;
};
